import {
  ControlArea as SharedControlArea,
  LayoutSwitch,
  type LayoutSwitchOption,
} from '@/components/ControlArea';
import { JournalLayout } from '@/features/approving_invoice_reports/components/PaymentDetailDrawer/Detail/LeftContent/JournalInfo/hooks/useJournalLayout';
import { useTranslation } from '@/i18n';
import { toCommaSeparated } from '@/utils/convert/toCommaSeparated';
import { IconCard, IconTable } from '@moneyforward/ap-frontend-components';
import { FC, memo, useMemo } from 'react';

export type Props = {
  count: number;
  layout: JournalLayout;
  onLayoutChange: (layout: JournalLayout) => void;
};

export const ControlArea: FC<Props> = memo(
  ({ count, layout, onLayoutChange }) => {
    const { t } = useTranslation();

    // Ordered to match the design's card-then-table visual layout; the order
    // also decides arrow-key traversal in the shared `LayoutSwitch`.
    const options = useMemo<LayoutSwitchOption<JournalLayout>[]>(
      () => [
        {
          value: 'card',
          label: t('journal_entry.layout_card'),
          tooltip: t('journal_entry.layout_card_tooltip'),
          icon: <IconCard />,
          testId: 'journal-layout-card',
        },
        {
          value: 'table',
          label: t('journal_entry.layout_table'),
          tooltip: t('journal_entry.layout_table_tooltip'),
          icon: <IconTable />,
          testId: 'journal-layout-table',
        },
      ],
      [t]
    );

    return (
      <SharedControlArea
        testId='journal-control-area'
        count={
          // `count` is reserved by i18next for pluralisation, so the
          // interpolation variable is `total`.
          t('journal_entry.count', { total: toCommaSeparated(count) })
        }
      >
        <LayoutSwitch
          layout={layout}
          onChange={onLayoutChange}
          options={options}
          ariaLabel={t('journal_entry.layout_label')}
        />
      </SharedControlArea>
    );
  }
);
ControlArea.displayName = 'ControlArea';
